import { useStoreWithEqualityFn } from "zustand/traditional";
import { usePlayerEngine } from "../../../runtime/provider";
import { getPlayerState } from "./actions";
import { useCallback } from "react";
import type { PlayerStore } from "./types";
import {
  selectActiveQueue,
  selectActiveQueueType,
  selectActiveSong,
  selectActiveSongId,
  selectActiveCollectionType,
  selectActiveCollectionId,
  selectActiveCollectionName,
  selectIsPlaying,
  selectIsBuffering,
  // selectProgress removed - progress now in shared values
  // selectBufferedPosition removed — buffered now in shared values
  selectActiveSongDuration,
  selectSeekable,
  selectForyouQueue,
  selectPlaylistQueue,
  selectHasHydrated,
} from "./selectors";
import { shallow } from "zustand/shallow";

export function usePlayer<TSelected>(
  selector: (state: PlayerStore) => TSelected,
): TSelected {
  return useStoreWithEqualityFn(usePlayerEngine().store, selector, shallow);
}

export const useIsPlaying = () => usePlayer(selectIsPlaying);
export const useIsBuffering = () => usePlayer(selectIsBuffering);
export const useActiveSongId = () => usePlayer(selectActiveSongId);
export const useActiveSongDuration = () => usePlayer(selectActiveSongDuration);
export const useActiveSong = () => usePlayer(selectActiveSong);

export const usePlayerControls = () => {
  const playerActions = usePlayerActions();
  return {
    play: playerActions.play,
    pause: playerActions.pause,
    togglePlay: playerActions.togglePlay,
    next: playerActions.next,
    previous: playerActions.previous,
    seekTo: playerActions.seekTo,
    startSeek: playerActions.startSeek,
    seekPreview: playerActions.seekPreview,
    endSeek: playerActions.endSeek,
  };
};

/**
 * Hook to access progress shared values.
 * These values update at 100ms intervals and NEVER trigger React re-renders.
 *
 * Components should read directly from progressState shared values in Reanimated
 * worklets or use useAnimatedReaction for reactive updates.
 *
 * This hook returns the shared value references for direct use in Reanimated.
 */
export const useProgress = () => {
  const { progressState } = usePlayerEngine().progress;
  // Return shared value references directly - these update without React re-renders
  return {
    position: progressState.position,
    duration: progressState.duration,
    buffered: progressState.buffered,
  };
};

// For components that need seekable state (e.g., during ads)
export const useSeekable = () => usePlayer(selectSeekable);

export const useActiveQueue = () => usePlayer(selectActiveQueue);
export const useActiveQueueType = () => usePlayer(selectActiveQueueType);
export const useActiveCollectionId = () => usePlayer(selectActiveCollectionId);
export const useActiveCollectionType = () =>
  usePlayer(selectActiveCollectionType);
export const useActiveCollectionName = () =>
  usePlayer(selectActiveCollectionName);

export const useForyouQueue = () => usePlayer(selectForyouQueue);
export const usePlaylistQueue = () => usePlayer(selectPlaylistQueue);
export const useHasHydrated = () => usePlayer(selectHasHydrated);

export const usePlayerActions = () => usePlayerEngine().actions;
export const usePlayerStoreApi = () => usePlayerEngine().store;
export const usePlayerStateReader = () => {
  const store = usePlayerStoreApi();
  return useCallback(() => getPlayerState(store), [store]);
};

export { usePlayerEngine } from "../../../runtime/provider";
